/**
 * The percentage slider: 4px `bg-active` track, `accent-solid` fill (or
 * `up-solid`/`down-solid` once a side is chosen), 12px thumb with a 2px
 * `bg-canvas` ring, snap marks at 25/50/75/100 (components.md §3).
 *
 * Keyboard is arrows ±1% and shift+arrows ±10%, which is the documented
 * behaviour and also the reason this is a real `<input type="range">` rather
 * than a div with pointer handlers: the native control already gives the ARIA
 * semantics, the keyboard model and the touch target, and shift-stepping is the
 * only part that needs adding.
 */

import { cx } from '@/lib/cx'

export type SliderTone = 'accent' | 'up' | 'down'

const FILL: Record<SliderTone, string> = {
  accent: 'var(--ud-color-accent-solid)',
  up: 'var(--ud-color-up-solid)',
  down: 'var(--ud-color-down-solid)',
}

export const SNAP_MARKS = [25, 50, 75, 100] as const

/**
 * **Where each mark sits: under the thumb at that value.** They were four
 * items spread with `justify-between`, which put `25%` at the track's left end
 * (where the thumb is at 0%) and `50%` a third of the way along, so a thumb at
 * 53% sat nearer the `75%` label than the `50%` one.
 *
 * A range thumb's centre travels from half its width to the width less half
 * its width — `6px + (100% − 12px) × v`, for the 12px thumb in `base.css`.
 * `100%` is right-aligned at the track's end rather than centred on it, so the
 * label never hangs past the ticket's edge. Static strings, because Tailwind
 * only generates classes it can read in the source.
 */
const MARK_POSITION: Record<(typeof SNAP_MARKS)[number], string> = {
  25: 'left-[calc(6*var(--ud-u)+(100%-12*var(--ud-u))*0.25)] -translate-x-1/2',
  50: 'left-[calc(6*var(--ud-u)+(100%-12*var(--ud-u))*0.5)] -translate-x-1/2',
  75: 'left-[calc(6*var(--ud-u)+(100%-12*var(--ud-u))*0.75)] -translate-x-1/2',
  100: 'right-0',
}

export interface SliderProps {
  value: number
  onChange: (value: number) => void
  tone?: SliderTone
  label: string
  disabled?: boolean
  className?: string
}

export function Slider({
  value,
  onChange,
  tone = 'accent',
  label,
  disabled,
  className,
}: SliderProps) {
  const clamp = (v: number) => Math.min(100, Math.max(0, Math.round(v)))

  return (
    <div className={cx('flex flex-col gap-1', className)}>
      <div className="relative flex h-[var(--ud-control-height)] items-center">
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={value}
          disabled={disabled}
          aria-label={label}
          onChange={(e) => onChange(clamp(Number(e.target.value)))}
          onKeyDown={(e) => {
            if (!e.shiftKey) return
            const step =
              e.key === 'ArrowRight' || e.key === 'ArrowUp'
                ? 10
                : e.key === 'ArrowLeft' || e.key === 'ArrowDown'
                  ? -10
                  : 0
            if (step === 0) return
            e.preventDefault()
            onChange(clamp(value + step))
          }}
          className="ud-slider min-h-[var(--ud-tap-min)] md:min-h-0 w-full"
          style={
            {
              '--ud-slider-fill': FILL[tone],
              '--ud-slider-pct': `${value}%`,
            } as React.CSSProperties
          }
        />
      </div>
      <div className="relative">
        {/* Holds the row's height: the marks themselves are placed on the track. */}
        <span
          aria-hidden="true"
          className="ud-num invisible inline-flex min-h-[var(--ud-tap-min)] items-center text-micro md:min-h-0"
        >
          100%
        </span>
        {SNAP_MARKS.map((mark) => (
          <button
            key={mark}
            type="button"
            // Not a tab stop, for the reason `<Input>`'s steppers are not: the
            // control beside them already does the whole job from the keyboard
            // (the range input takes arrows, and Shift+arrow steps by ten), so
            // four extra stops would put four presses between the order type and
            // the Buy button and buy a keyboard user nothing. They stay
            // clickable, which is what they are for.
            tabIndex={-1}
            disabled={disabled}
            onClick={() => onChange(mark)}
            className={cx(
              'ud-num text-micro transition-colors duration-[var(--ud-duration-instant)]',
              // The snap marks are 14px of text and are a real control below
              // 768px, where they are the fastest way to size an order.
              'inline-flex min-h-[var(--ud-tap-min)] min-w-[var(--ud-tap-min)] md:min-h-0 md:min-w-0 items-center justify-center',
              'absolute top-0 h-full',
              MARK_POSITION[mark],
              'focus-visible:shadow-focus focus-visible:outline-none',
              value === mark ? 'text-text-primary' : 'text-text-tertiary hover:text-text-secondary',
            )}
          >
            {mark}%
          </button>
        ))}
      </div>
    </div>
  )
}
